/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/window.d.ts" />

/**
 * BlockService
 */

namespace Application.Services {
  "use strict";

  import Block = Models.Block;
  import IBlock = Models.IBlock;
  import IHalList = Models.IHalList;
  import IHalElement = Models.IHalElement;
  import BlockType = Models.BlockType;
  import HalConverter = Models.HalConverter;
  import Utils = Common.Utils;
  import Category = Models.Category;
  import IRedundantBlocks = Application.Models.IRedundantBlocks;
  import RedundantBlocks = Application.Models.RedundantBlocks;
  import INetworkNode = Application.Models.NetworkNode;
  import IBlockNameParameters = Application.Models.IBlockNameParameters;
  import IBlockName = Application.Models.IBlockName;
  import BlockConnections = Application.Models.BlockConnections;

  interface BlockTypeByBlockNaturalKeyMap {
    [naturalKey: string]: BlockType;
  }

  interface BlockTypesByLevelMap {
    [level: string]: BlockType[];
  }

  /**
   * BlockService
   */
  export class BlockService {
    public static $name: string = "blockService";
    public static $inject: string[] = [
      "$http",
      "$q",
      "networkNodeService",
      "backendService",
      "$rootScope",
    ];

    private http: ng.IHttpService;
    private qService: ng.IQService;
    private networkNodeService: NetworkNodeService;
    private backendService: BackendService;
    private $rootScope: IFlexRootScope;

    private backendBaseUrl: string = window.backendUrl;
    // private servicePath: string = 'flexocore/';
    private servicePath: string = "";
    private backendUrl: string =
      this.backendBaseUrl + this.servicePath + "blocks";
    private blockTypeBackendUrl: string =
      this.backendBaseUrl + this.servicePath + "blockTypes";
    private categoriesBackendUrl: string =
      this.backendBaseUrl + this.servicePath + "categories";
    private applicationsBackendUrl: string =
      this.backendBaseUrl + this.servicePath + "applications";

    private blockTypeByNaturalKey: BlockTypeByBlockNaturalKeyMap;
    private blockTypesByInputAndLevel: BlockTypesByLevelMap = {};
    private blockTypesByOutputAndLevel: BlockTypesByLevelMap = {};

    constructor(
      $http: ng.IHttpService,
      $q: ng.IQService,
      networkNodeService: NetworkNodeService,
      backendService: BackendService,
      $rootScope: IFlexRootScope,
      streamPipeService: StreamPipeService
    ) {
      this.http = $http;
      this.qService = $q;
      this.networkNodeService = networkNodeService;
      this.backendService = backendService;

      this.$rootScope = $rootScope;

      this.$rootScope.$on(
        "vars.isUserLoggedIn",
        (event: ng.IAngularEvent, args: boolean) => {
          if (args) {
            this.loadAllBlockTypes();
          }
        }
      );
    }

    public getAllBlocks(callback: (networks: IBlock[]) => void): void {
      this.getAllBlocksPromise().then((data: IBlock[]) => {
        callback(data);
      });
    }

    public findBlocksByDirectionAndType(
      direction: string,
      type: string,
      callback: (blocks: IBlock[]) => void
    ): void {
      this.findBlocksByDirectionAndTypePromise(direction, type).then(
        (blocks: IBlock[]) => {
          callback(blocks);
        }
      );
    }

    public findBlocksByInputOfType(
      type: string,
      callback: (blocks: IBlock[]) => void
    ): void {
      this.findBlocksByInputOfTypePromise(type).then((blocks: IBlock[]) => {
        callback(blocks);
      });
    }

    public findBlocksByOutputOfType(
      type: string,
      callback: (blocks: IBlock[]) => void
    ): void {
      this.findBlocksByOutputOfTypePromise(type).then((blocks: IBlock[]) => {
        callback(blocks);
      });
    }

    public findSourceBlocksPromise(
      projection?: "simple"
    ): ng.IPromise<IBlock[]> {
      let url: string = this.backendUrl
        .concat("/search/findAllByBlockTypePipePosition?")
        .concat(
          Utils.encodeQueryData({
            pipePosition: "SOURCE",
          })
        );
      if (projection) url = url + "&projection=" + projection;
      return this.executeBlockSearchSimple(url);
    }

    public findSinkBlocksPromise(projection?: "simple"): ng.IPromise<IBlock[]> {
      let url: string = this.backendUrl
        .concat("/search/findAllByBlockTypePipePosition?")
        .concat(
          Utils.encodeQueryData({
            pipePosition: "SINK",
          })
        );
      if (projection) url = url + "&projection=" + projection;
      return this.executeBlockSearchSimple(url);
    }

    public findBlocksByInputOfTypePromise(type: string): ng.IPromise<IBlock[]> {
      let url: string = this.backendUrl
        .concat("/search/findBlocksByInputOfType?")
        .concat(
          Utils.encodeQueryData({
            type: type,
          })
        );
      return this.executeBlockSearch(url);
    }

    public loadAllBlockTypes(): void {
      if (!this.blockTypeByNaturalKey) {
        this.blockTypeByNaturalKey = {};
        const url: string = `${this.blockTypeBackendUrl}?size=1000`;
        this.executeBlockTypeSearchUrl(url).then((blockTypes: BlockType[]) => {
          blockTypes.forEach((blockType: BlockType) => {
            this.blockTypeByNaturalKey[blockType.naturalKey.toString()] =
              blockType;
          });
        });
      }
    }

    public findBlockTypesByInputLevel(level: string): ng.IPromise<BlockType[]> {
      if (!this.blockTypesByInputAndLevel[level]) {
        let url: string = this.blockTypeBackendUrl
          .concat("/search/blockTypesByLevelAndDirection?")
          .concat(
            Utils.encodeQueryData({
              level: level,
              direction: BlockConnections.DIRECTION_INPUT,
            })
          );
        return this.executeBlockTypeSearchUrl(url).then(
          (blockTypes: BlockType[]) => {
            this.blockTypesByInputAndLevel[level] = blockTypes;
            return blockTypes;
          }
        );
      } else {
        const deferred: ng.IDeferred<BlockType[]> = this.qService.defer();
        deferred.resolve(this.blockTypesByInputAndLevel[level]);
        return deferred.promise;
      }
    }

    public findBlockTypesByOutputLevel(
      level: string
    ): ng.IPromise<BlockType[]> {
      if (!this.blockTypesByOutputAndLevel[level]) {
        let url: string = this.blockTypeBackendUrl
          .concat("/search/blockTypesByLevelAndDirection?")
          .concat(
            Utils.encodeQueryData({
              level: level,
              direction: BlockConnections.DIRECTION_OUTPUT,
            })
          );
        return this.executeBlockTypeSearchUrl(url).then(
          (blockTypes: BlockType[]) => {
            this.blockTypesByOutputAndLevel[level] = blockTypes;
            return blockTypes;
          }
        );
      } else {
        const deferred: ng.IDeferred<BlockType[]> = this.qService.defer();
        deferred.resolve(this.blockTypesByOutputAndLevel[level]);
        return deferred.promise;
      }
    }

    public findBlocksByOutputOfTypePromise(
      type: string
    ): ng.IPromise<IBlock[]> {
      let url: string = this.backendUrl
        .concat("/search/findBlocksByOutputOfType?")
        .concat(
          Utils.encodeQueryData({
            type: type,
          })
        );
      return this.executeBlockSearch(url);
    }

    public findBlocksBySourceLevel(type: string): ng.IPromise<Block[]> {
      let url: string = this.backendUrl
        .concat("/search/blocksByLevelAndPosition?")
        .concat(
          Utils.encodeQueryData({
            level: type,
            position: "SOURCE",
          })
        );
      return this.executeBlockSearch(url);
    }

    public findBlockTypesBySourceLevel(type: string): ng.IPromise<BlockType[]> {
      let url: string = this.blockTypeBackendUrl
        .concat("/search/blockTypesByLevelAndPosition?")
        .concat(
          Utils.encodeQueryData({
            level: type,
            position: "SOURCE",
          })
        );
      return this.executeBlockTypeSearchUrl(url);
    }

    public findBlocksBySinkLevel(type: string): ng.IPromise<Block[]> {
      let url: string = this.backendUrl
        .concat("/search/blocksByLevelAndPosition?")
        .concat(
          Utils.encodeQueryData({
            level: type,
            position: "SINK",
          })
        );
      return this.executeBlockSearch(url);
    }

    public findBlockTypesBySinkLevel(type: string): ng.IPromise<BlockType[]> {
      let url: string = this.blockTypeBackendUrl
        .concat("/search/blockTypesByLevelAndPosition?")
        .concat(
          Utils.encodeQueryData({
            level: type,
            position: "SINK",
          })
        );
      return this.executeBlockTypeSearchUrl(url);
    }

    public findBlocksByNetworkNodeHrefAndBlockTypeNaturalKey(
      networkNodeHref: string,
      naturalKey: string
    ): ng.IPromise<Block[]> {
      let url: string = this.backendUrl
        .concat("/search/blocksInNodeByBlockTypeNaturalKey?")
        .concat(
          Utils.encodeQueryData({
            networkNodeHref: networkNodeHref,
            naturalKey: naturalKey,
          })
        );
      return this.executeBlockSearch(url);
    }

    public findById(id: string | number): ng.IPromise<IBlock> {
      return this.http
        .get(this.backendUrl + "/" + id)
        .then(
          (result: ng.IHttpPromiseCallbackArg<Models.IHalElement<Block>>) => {
            let halElement: Models.IHalElement<Block> = result.data;
            let converted: Block = HalConverter.convertElement(
              halElement,
              Block.Empty
            );
            return converted;
          }
        );
    }

    public findByName(name: string): ng.IPromise<IBlock> {
      return this.http
        .get(this.backendUrl + "/search/findOneByName?name=" + name)
        .then(
          (result: ng.IHttpPromiseCallbackArg<Models.IHalElement<Block>>) => {
            let halElement: Models.IHalElement<Block> = result.data;
            let converted: Block = HalConverter.convertElement(
              halElement,
              Block.Empty
            );
            return converted;
          }
        );
    }

    public getNodeFromBlockId(
      blockId: number
    ): ng.IPromise<Models.NetworkNode> {
      console.log("getNodeFromBlockId");
      return this.findById(blockId.toString()).then((block: IBlock) => {
        return this.getNodeFromBlock(block);
      });
    }

    public getNodeFromBlockName(
      blockName: string
    ): ng.IPromise<Models.NetworkNode> {
      return this.findByName(blockName).then((block: IBlock) => {
        return this.getNodeFromBlock(block);
      });
    }

    public getNodeFromBlock(
      block: Models.IBlock
    ): ng.IPromise<Models.NetworkNode> {
      return this.networkNodeService.getByIdPromise(
        Common.Utils.idFromHref(block.networkNodeHref)
      );
    }

    public getCategories(): ng.IPromise<Models.Category[]> {
      return this.backendService
        .get(this.categoriesBackendUrl + "?size=5000")
        .then(
          (
            result: ng.IHttpPromiseCallbackArg<Models.IHalList<Models.Category>>
          ) => {
            let halElement: Models.IHalList<Models.Category> = result.data;
            return Models.HalConverter.convertList(
              halElement,
              Models.Category.Empty
            );
          }
        );
    }

    public getCategoriesWithoutBlocks(): ng.IPromise<Category[]> {
      return this.backendService
        .get(this.categoriesBackendUrl + "/withoutBlocks")
        .then((result: ng.IHttpPromiseCallbackArg<Models.Category[]>) => {
          return result.data;
        });
    }

    public getCategoriesByBlockId(
      blockId: string
    ): ng.IPromise<Models.Category[]> {
      return this.backendService
        .get(this.backendUrl + "/" + blockId + "/categories")
        .then(
          (
            result: ng.IHttpPromiseCallbackArg<Models.IHalList<Models.Category>>
          ) => {
            let halElement: Models.IHalList<Models.Category> = result.data;
            return Models.HalConverter.convertList(
              halElement,
              Models.Category.Empty
            );
          }
        );
    }

    public addCategoryToBlock(
      blockId: string,
      category: Category
    ): ng.IPromise<Models.ICategory> {
      return this.backendService
        .put(this.backendUrl + "/addCategoryToBlock/" + blockId, category)
        .then(
          (
            result: ng.IHttpPromiseCallbackArg<Models.IHalElement<Category>>
          ) => {
            let halElement: Models.IHalElement<Category> = result.data;
            let converted: Category = HalConverter.convertElement(
              halElement,
              Category.Empty
            );
            return converted;
          }
        );
    }

    public removeCategoryFromBlock(
      blockId: string,
      category: Category
    ): ng.IPromise<Models.IBlock> {
      return this.backendService
        .put(this.backendUrl + "/removeCategoryFromBlock/" + blockId, category)
        .then(
          (result: ng.IHttpPromiseCallbackArg<Models.IHalElement<Block>>) => {
            let halElement: Models.IHalElement<Block> = result.data;
            let converted: Block = HalConverter.convertElement(
              halElement,
              Block.Empty
            );
            return converted;
          }
        );
    }

    public saveBlock(block: IBlock): ng.IPromise<IBlock> {
      if (!block.href || block.href === undefined || block.href === "") {
        return this.createBlock(block);
      } else {
        return this.updateBlock(block);
      }
    }

    public createBlock(block: IBlock): ng.IPromise<IBlock> {
      return this.backendService
        .post(this.backendUrl, {
          name: block.name,
          locked: block.locked,
        })
        .then(
          (result: ng.IHttpPromiseCallbackArg<Models.IHalElement<IBlock>>) => {
            let halElement: IHalElement<Block> = result.data;
            let converted: Block = HalConverter.convertElement(
              halElement,
              Models.Block.Empty
            );
            return converted;
          }
        );
    }

    public generateAppAndBlock(
      nodeId: number,
      blockName: string,
      blockTypeId: number,
      blockConfig: string
    ): ng.IPromise<IBlock> {
      return this.backendService
        .post(`${this.applicationsBackendUrl}/generateAppAndBlock/${nodeId}`, {
          name: blockName,
          blockTypeId: blockTypeId,
          blockConfig: JSON.parse(blockConfig),
        })
        .then(
          (result: ng.IHttpPromiseCallbackArg<Models.IHalElement<IBlock>>) => {
            let halElement: IHalElement<Block> = result.data;
            let converted: Block = HalConverter.convertElement(
              halElement,
              Models.Block.Empty
            );
            return converted;
          }
        );
    }

    public deleteBlock(id: number): ng.IPromise<boolean> {
      return this.backendService.delete(`${this.backendUrl}/${id}`);
    }

    public updateBlock(block: IBlock): ng.IPromise<IBlock> {
      // unselecting blocks fails with redundancy
      if (!block.href) throw "No href in block";

      let blockUrl: string = this.fixBlockUrl(block);
      blockUrl = this.backendUrl + "/" + block.id;

      return this.backendService
        .put(blockUrl, {
          name: block.name,
          config: block.config,
          locked: block.locked,
          lockedComment: block.lockedComment,
          lockedName: block.lockedName,
          suffix: block.suffix,
        })
        .then(
          (
            result: ng.IHttpPromiseCallbackArg<
              Models.IHalElement<Models.INetworkNode>
            >
          ) => {
            let halElement: IHalElement<Block> = result.data;
            let converted: Block = HalConverter.convertElement(
              halElement,
              Block.Empty
            );
            return converted;
          }
        );
    }

    public getAllBlocksPromise(): ng.IPromise<Block[]> {
      let url: string = this.backendUrl.concat("/search/all");
      return this.executeNoSearch(url);
    }

    public getAllMultiviewerRoomBlocks(blocks: number[]): ng.IPromise<Block[]> {
      return this.backendService
        .post(this.backendUrl + "/multiviewerRoomBlocks", blocks)
        .then((result: ng.IHttpPromiseCallbackArg<Models.IBlock[]>) => {
          return result.data;
        });
    }

    public deleteRedundantGroups(node: Application.Models.INetworkNode): void {
      if (node.type.id === 13) {
        node.blocks.forEach((block: Models.IBlock) => {
          this.deleteRedundantGroup(block);
        });
      }
    }

    public deleteRedundantGroup(block: IBlock | IBlockName): void {
      if (block.config.redundancyGroup !== undefined) {
        let redundancyIds =
          block.config.redundancyGroup.redundancyIds.split("-");
        let blockToUpdate = redundancyIds[1];
        if (Number(redundancyIds[0]) !== block.id) {
          blockToUpdate = redundancyIds[0];
        }
        this.findById(blockToUpdate).then((response: Models.IBlock) => {
          delete response.config.redundancyGroup;
          this.updateBlock(response);
        });
      }
    }

    public getBlocksByNaturalKeyPromise(
      naturalKeys: string[]
    ): ng.IPromise<Block[]> {
      let url: string = this.backendUrl
        .concat("/search/blocksByNaturalKeys?naturalKey=")
        .concat(naturalKeys.join("&naturalKey="));
      return this.executeNoSearch(url);
    }

    public getBlocksByConnection(
      typeOfConnection: string
    ): ng.IPromise<Block[]> {
      let url: string = this.backendUrl
        .concat("/search/blocksByConnection?connectionType=")
        .concat(typeOfConnection);
      return this.executeNoSearch(url);
    }

    public getBlocksByConnectionAndBlockTypes(
      typeOfConnection: string,
      blockTypes: number[]
    ): ng.IPromise<Block[]> {
      let url: string = this.backendUrl
        .concat("/search/getBlocksByConnectionAndBlockTypes?connectionType=")
        .concat(typeOfConnection)
        .concat("&blockTypes=")
        .concat(blockTypes.toString());
      return this.executeNoSearch(url);
    }

    public getDefaultBlocksNames(
      nodeTypeId: string,
      nodeName: string,
      numberOfBlocks: number
    ): ng.IPromise<Models.IBlockName[]> {
      const body: IBlockNameParameters = {
        nodeName: nodeName,
        numberOfBlocks: numberOfBlocks,
      };
      return this.getDefaultBlocksNamesWithParameters(nodeTypeId, body);
    }

    /**
     * Gets the default name for the blocks of a network node
     * @param nodeTypeId type of network node
     * @param blockNameParameters {@link IBlockNameParameters} with the request parameters for the backend call
     * @return promise containing an array with the block names
     */
    public getDefaultBlocksNamesWithParameters(
      nodeTypeId: string,
      blockNameParameters: IBlockNameParameters
    ): ng.IPromise<Models.IBlockName[]> {
      return this.backendService
        .post(
          this.backendBaseUrl + "netwotkNodeTypeApps/blockNames/" + nodeTypeId,
          blockNameParameters
        )
        .then((result: ng.IHttpPromiseCallbackArg<Models.IBlockName[]>) => {
          return result.data;
        });
    }

    public blocksInNode(
      href: number,
      withType: boolean = true
    ): ng.IPromise<IBlock[]> {
      let url: string = this.backendUrl.concat("/search/blocksInNode?").concat(
        Utils.encodeQueryData({
          networkNodeHref: href,
        })
      );

      return this.executeBlockSearch(url, withType);
    }

    public regenerateBlocks(nodeId: number): ng.IPromise<boolean> {
      return this.backendService
        .get(this.backendUrl + "/regenerateBlocks/" + nodeId)
        .then((result: ng.IHttpPromiseCallbackArg<boolean>) => {
          return result.data;
        });
    }

    public getNetworkNodeIcon(block: Models.IBlock) {
      return this.networkNodeService.getNetworkNodeIcon(
        block.networkNodeTypeHref
      );
    }

    /**
     * Call the backend to get the redundant blocks of the primary DR5000 decoder.
     * It also retrieve the encoder blocks if they are connected to the decoder.
     */
    public getRedundantBlocks(id: number): ng.IPromise<IRedundantBlocks> {
      return this.http
        .get(this.backendUrl + "/redundantBlocks/" + id)
        .then(
          (
            result: ng.IHttpPromiseCallbackArg<
              Models.IHalElement<IRedundantBlocks>
            >
          ) => {
            let halElement: Models.IHalElement<IRedundantBlocks> = result.data;
            let converted: IRedundantBlocks = HalConverter.convertElement(
              halElement,
              RedundantBlocks.Empty
            );
            return converted;
          }
        );
    }

    public getConnectedBlocks(id: number): ng.IPromise<Block[]> {
      return this.http
        .get(this.backendUrl + "/connectedBlocks/" + id)
        .then((result: ng.IHttpPromiseCallbackArg<IHalList<Block>>) => {
          let halElement: IHalList<Block> = result.data;
          let converted: Block[] = HalConverter.convertList(
            halElement,
            Block.Empty
          );
          return converted;
        });
    }

    private findBlocksByDirectionAndTypePromise(
      direction: string,
      type: string
    ): ng.IPromise<IBlock[]> {
      let url: string = this.backendUrl
        .concat("/search/findBlocksByDirectionAndType?")
        .concat(
          Utils.encodeQueryData({
            direction: direction,
            type: type,
          })
        );
      return this.executeBlockSearch(url);
    }

    private executeNoSearch(url: string): ng.IPromise<Block[]> {
      return this.http
        .get(url)
        .then((result: ng.IHttpPromiseCallbackArg<IHalList<Block>>) => {
          let halElement: IHalList<Block> = result.data;
          let converted: Block[] = HalConverter.convertList(
            halElement,
            Block.Empty
          );
          return converted;
        });
    }

    private executeBlockSearch(
      url: string,
      withType: boolean = true
    ): ng.IPromise<Block[]> {
      return this.http
        .get(url)
        .then((result: ng.IHttpPromiseCallbackArg<IHalList<Block>>) => {
          let halElement: IHalList<Block> = result.data;
          let converted: Block[] = HalConverter.convertList(
            halElement,
            Block.Empty
          );

          if (!withType) {
            return converted;
          }

          converted.forEach((block: Block) => {
            block.blockType =
              this.blockTypeByNaturalKey[block.blockTypeNaturalKey];
          });

          return converted;
        });
    }

    /**
     * Be careful! This method only retrieve blocks, not its type and blockConnections.
     * Use only when you know you don't need those subElements filled in.
     */
    private executeBlockSearchSimple(url: string): ng.IPromise<IBlock[]> {
      return this.http
        .get(url)
        .then((result: ng.IHttpPromiseCallbackArg<IHalList<Block>>) => {
          let halElement: IHalList<Block> = result.data;
          let converted: Block[] = HalConverter.convertList(
            halElement,
            Block.Empty
          );
          return converted;
        });
    }

    private executeBlockTypeSearchUrl(url: string): ng.IPromise<BlockType[]> {
      return this.http
        .get(url)
        .then((result: ng.IHttpPromiseCallbackArg<IHalList<BlockType>>) => {
          let halElement: IHalList<BlockType> = result.data;
          let converted: BlockType[] = HalConverter.convertList(
            halElement,
            BlockType.Empty
          );
          return converted;
        });
    }

    public fixBlockUrl(block: IBlock): string {
      let href: string = block.href;
      if (href.indexOf(this.backendBaseUrl) < 0) {
        return this.backendUrl + "/" + Common.Utils.idFromHref(href);
      } else {
        return href;
      }
    }

    public getMonitorBlocks(monitorDevices: INetworkNode[]) {
      let promisesArray: any[] = [];
      let monitorBlocks: IBlock[] = [];

      monitorDevices.forEach((node: INetworkNode) => {
        promisesArray.push(this.blocksInNode(Number(node.id)));
      });
      return Promise.all(promisesArray).then((devices) => {
        devices.forEach((device) => {
          device.forEach((block: Block) => {
            if (block.blockTypeNaturalKey == "MVP_PLAYOUT" || block.blockTypeNaturalKey == "Intinor_DR") {
              monitorBlocks.push(block);
            }
          });
        });
        monitorBlocks = monitorBlocks.filter((block: IBlock) => {
          return block.workflowMode === "operation";
        });
        return monitorBlocks;
      });
    }

    /**
     * Checks in an array of blocks contains a locked block
     * @param blocks array of blocks
     * @return boolean
     */
    public hasLockedBlock(blocks: Models.IBlock[]): boolean {
      return blocks.some((el: IBlock) => el.locked);
    }

    /**
     * Gets one locked block from an array of blocks. If there is not any block returns undefined
     * @param blocks array of blocks
     * @return IBlock or undefined
     */
    public getLockedBlock(blocks: Models.IBlock[]): Models.IBlock {
      const lockedBlocks: Models.IBlock[] = blocks.filter(
        (el: IBlock) => el.locked
      );
      return lockedBlocks.length > 0 ? lockedBlocks[0] : undefined;
    }
  }
}
